<template>
    <MainComponent :id="anchorsPageProfile.appraisalsSection" :class="{'appraisal-section--mf': mf}">
        <template #title>
            <h2 class="appraisal-section__title">{{ t('profile.resume.appraisals.title') }}</h2>
        </template>
        <template #title-action>
            <VJoyWrapper class="appraisal-section__title-action" direction="row" justify="flex-end" no-margin>
                <LazyTranslateButton v-if="isTranslationActivated && hasAppraisals" section="appraisal" />
                <AppraisalSortMenu
                    v-if="visibleAppraisals.length > 1"
                    v-model:selected-sort="selectedSort"
                    class="appraisal-section__sort-menu--desktop"
                />
            </VJoyWrapper>
        </template>
        <template v-if="appraisals" #content>
            <EmptyAppraisalsEditMode v-if="!hasAppraisals && isEditionEnabled" />
            <template v-else-if="hasAppraisals">
                <VJoyWrapper align="flex-start" class="appraisal-section__rating-container" direction="row" justify="flex-start">
                    <OverallRating :appraisal-counter="appraisals.numberOfAppraisalsWithRating || 0" :ratings="appraisals.ratings" />
                    <AppraisalSortMenu
                        v-if="visibleAppraisals.length > 1"
                        v-model:selected-sort="selectedSort"
                        class="appraisal-section__sort-menu--mobile"
                    />
                    <VJoyWrapper
                        :no-margin="true"
                        align="flex-start"
                        class="appraisal-section__list"
                        direction="column"
                        justify="flex-start"
                        row-gap="4"
                    >
                        <NoDisplayedAppraisals v-if="noAppraisalToDisplay" :freelancer-name="profile?.firstname" />
                        <AppraisalCard
                            v-for="appraisal in visibleAppraisals"
                            v-else
                            :key="appraisal.appraisalId"
                            :appraisal="appraisal"
                            :is-edit-mode="isEditionEnabled"
                            :mf="mf"
                            class="appraisal-section__item"
                            @see-more-clicked-when-locked="$emit('seeMoreClickedWhenLocked')"
                        />
                        <HiddenAppraisalIndicator v-if="displayHiddenAppraisalsByClientIndicator" :hidden-counter="hiddenByClientCounter" />
                        <VJoyText v-if="displayHiddenAppraisalsLabel" class="appraisal-section__private-count" preset="primary-300" tag-name="span">
                            {{ t('profile.section.appraisals.hidden.count', {count: hiddenByAnyoneCounter}) }}
                        </VJoyText>
                    </VJoyWrapper>
                </VJoyWrapper>
                <ShowMoreByItemCount
                    :label="t('profile.section.appraisals.show.all.label.v2', {count: totalDisplayableAppraisalsCounter})"
                    :limit="APPRAISAL_LIMIT_DISPLAYED"
                    :locked="{
                        allowHref: !mf,
                        componentName: 'profile-appraisals-section',
                        targetId: anchorsPageProfile.appraisalsSection,
                        label: t('profile.section.appraisals.show.all.label.v2', {count: totalDisplayableAppraisalsCounter}),
                    }"
                    :total="totalDisplayableAppraisalsCounter"
                    test-id="appraisals-show-more"
                    @collapse="isExpanded = false"
                    @expand="isExpanded = true"
                    @show-more-clicked-when-locked="$emit('showMoreClickedWhenLocked')"
                />
            </template>
        </template>
    </MainComponent>
</template>

<script lang="ts" setup>
    import {storeToRefs} from 'pinia';
    import {LazyTranslateButton} from '#components';
    import {computed, onMounted, onUnmounted, ref, useAuth, useTranslation} from '#imports';
    import {VJoyText, VJoyWrapper} from '@maltjoy/core-vue';
    import ShowMoreByItemCount from '~/components/_common/show-more/ShowMoreByItemCount.vue';
    import MainComponent from '~/components/main/common/MainComponent.vue';
    import {useProfilePageViewMode} from '~/composables/useProfilePageViewMode';
    import {anchorsPageProfile} from '~/config/anchors';
    import {eventBus} from '~/config/events';
    import AppraisalCard from '~/features/appraisals/ui/AppraisalCard.vue';
    import AppraisalSortMenu, {type AppraisalSortOption} from '~/features/appraisals/ui/AppraisalSortMenu.vue';
    import EmptyAppraisalsEditMode from '~/features/appraisals/ui/EmptyAppraisalsEditMode.vue';
    import HiddenAppraisalIndicator from '~/features/appraisals/ui/HiddenAppraisalIndicator.vue';
    import NoDisplayedAppraisals from '~/features/appraisals/ui/NoDisplayedAppraisals.vue';
    import OverallRating from '~/features/appraisals/ui/OverallRating.vue';
    import {bestRatingSorting, clientFirstSorting, mostRecentSorting, mostRelevantSorting} from '~/features/appraisals/utils/appraisalSorting';
    import {useProfileStore} from '~/store/profileStore';
    import {TranslationDirection, useTranslationStore} from '~/store/translation/translationStore';
    import type {Appraisal} from '~/types/domain/Appraisals';

    type Props = {
        mf?: boolean;
    };

    defineEmits(['showMoreClickedWhenLocked', 'seeMoreClickedWhenLocked']);

    const {mf = false} = defineProps<Props>();

    const APPRAISAL_LIMIT_DISPLAYED = 2;
    const {appraisals, profile} = storeToRefs(useProfileStore());
    const {isEditionEnabled} = useProfilePageViewMode();
    const {user} = storeToRefs(useAuth());
    const numberOfAppraisal = computed(() => appraisals.value?.appraisals.length);
    const isExpanded = ref(false);

    const hiddenByClientCounter = computed(
        () =>
            (
                appraisals.value?.appraisals.filter(
                    (appraisal: Appraisal) => appraisal.hiddenByClient && appraisal.clientAccountId !== user.value?.id,
                ) ?? []
            ).length,
    );

    const hiddenByAnyoneCounter = computed(
        () => (appraisals.value?.appraisals.filter((appraisal: Appraisal) => appraisal.hidden || appraisal.hiddenByClient) ?? []).length,
    );

    const displayHiddenAppraisalsByClientIndicator = computed(() => isEditionEnabled.value && hiddenByClientCounter.value > 0);
    const displayHiddenAppraisalsLabel = computed(() => !isEditionEnabled.value && hiddenByAnyoneCounter.value > 0 && !noAppraisalToDisplay.value);

    const totalDisplayableAppraisals = computed(() =>
        sortedAppraisals.value.filter((appraisal: Appraisal) => {
            if (appraisal.hiddenByClient && appraisal.clientAccountId !== user.value?.id) {
                return false;
            }
            if (!isEditionEnabled.value && appraisal.hidden) {
                return false;
            }
            return true;
        }),
    );

    const visibleAppraisals = computed(() =>
        isExpanded.value ? totalDisplayableAppraisals.value : totalDisplayableAppraisals.value.slice(0, APPRAISAL_LIMIT_DISPLAYED),
    );

    const totalDisplayableAppraisalsCounter = computed(() => totalDisplayableAppraisals.value.length);

    const noAppraisalToDisplay = computed(
        () => visibleAppraisals.value.length === 0 && !isEditionEnabled.value && (numberOfAppraisal.value ?? 0) > 0,
    );

    const appraisalsWithComment = computed(() => appraisals.value?.appraisals.filter((appraisal: Appraisal) => appraisal.comment) ?? []);
    const hasAppraisals = computed<boolean>(() => (appraisals.value && appraisals.value!.appraisals.length > 0) || false);

    // Sorting features
    const selectedSort = ref<AppraisalSortOption>('most-relevant');
    const sortedAppraisals = computed(() => {
        if (!appraisals.value) return [];

        let sorted: Appraisal[];
        switch (selectedSort.value) {
            case 'most-relevant':
                sorted = mostRelevantSorting([...appraisals.value.appraisals]);
                break;
            case 'best-rating':
                sorted = bestRatingSorting([...appraisals.value.appraisals]);
                break;
            case 'most-recent':
                sorted = mostRecentSorting([...appraisals.value.appraisals]);
                break;
            default:
                sorted = [...appraisals.value.appraisals];
        }
        return clientFirstSorting(sorted, user.value?.id);
    });

    //Translation features
    const {t} = useTranslation();
    const {isTranslationActivated} = storeToRefs(useTranslationStore());
    const {translateAll, replaceTranslationContent} = useTranslationStore();
    const translationSection = 'appraisals';
    const translationField = 'comment';

    async function translateAppraisals() {
        const appraisalCommentRequests = appraisalsWithComment.value.map((appraisal: Appraisal, index: number) => {
            return {
                section: translationSection,
                field: translationField,
                index,
                originalContent: appraisal.comment ?? ' ',
            };
        });

        await translateAll(appraisalCommentRequests);
        replaceTranslationContent(translationSection, translationField, TranslationDirection.TRANSLATE, switchTranslation);
    }

    function restoreAppraisals() {
        replaceTranslationContent(translationSection, translationField, TranslationDirection.RESTORE, switchTranslation);
    }

    function switchTranslation(content: string, index: number) {
        appraisalsWithComment.value[index].comment = content;
    }

    onMounted(() => {
        eventBus.on('translation:request', translateAppraisals);
        eventBus.on('translation:restore', restoreAppraisals);
    });

    onUnmounted(() => {
        eventBus.off('translation:request', translateAppraisals);
        eventBus.off('translation:restore', restoreAppraisals);
    });
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint' as joy;

    .appraisal-section {
        &--mf {
            border: none !important;
            border-radius: 0 !important;
            border-bottom: 1px solid var(--joy-color-neutral-30) !important;
            padding-bottom: var(--joy-core-spacing-8);
            margin-bottom: 0 !important;
            .appraisal-section__title {
                font-size: var(--joy-font-size-primary-400);
            }
        }

        &__sort-menu {
            &--mobile {
                display: block;
                width: 100%;
                order: 2;

                @include joy.screen_xs(min) {
                    display: none;
                }
            }

            &--desktop {
                display: none;

                @include joy.screen_xs(min) {
                    display: block;
                }
            }
        }

        &__rating-container {
            display: flex;
            gap: var(--joy-core-spacing-5);
            align-items: flex-start;
            flex-direction: column;

            @include joy.screen_xs(min) {
                flex-direction: row;
            }
        }

        &__list {
            width: 100%;
            min-width: 0;
            order: 3;

            @include joy.screen_xs(min) {
                flex: 1;
                width: auto;
                order: 0;
            }
        }

        &__item {
            padding-top: var(--joy-core-spacing-6);
            margin-top: var(--joy-core-spacing-2);
            border-top: 1px solid var(--joy-color-neutral-30);

            &:first-child {
                border-top: none;
                margin-top: 0;
                padding-top: 0;
            }
        }
    }
</style>
